import type { Sample } from '@/hooks/useSampleUpload'

/**
 * Decode + downsample a sample into a peak array suitable for
 * rendering a small waveform thumbnail on pad tiles.
 *
 * Peaks are stored as `[min_0, max_0, min_1, max_1, …]` — two
 * floats per bucket. Canvas renderers draw a vertical line per
 * bucket from `min` to `max`; that's the standard DAW-thumbnail
 * rendering strategy and fits small (< 100 px) widths well
 * without aliasing.
 *
 * Cache is module-level so multiple thumbnails referencing the
 * same sampleId share one decode, and switching between pads that
 * use the same sample doesn't re-fetch. Inflight promises are
 * also cached so a burst of thumbnail renders for the same sample
 * dedupes into one network round-trip.
 *
 * The cache is intentionally unbounded — local recordings + user
 * samples are small relative to browser memory; no eviction is
 * needed for expected session sizes. If a user piles up hundreds
 * of samples in one session we can add an LRU later.
 */

/** Default thumbnail resolution. 128 buckets draws cleanly at pad-
 *  tile widths (64–120 px) with enough detail for transients to
 *  read. Bumping this to 256 gives smoother thumbnails at larger
 *  widths; we keep it small by default to minimise per-sample
 *  memory. */
export const DEFAULT_PEAK_BUCKETS = 128

const cache = new Map<string, Float32Array>()
const pending = new Map<string, Promise<Float32Array | null>>()

/** Read the cached peaks for a sample without fetching. Returns
 *  `null` when the sample hasn't been decoded yet — consumers
 *  pair this with `getWaveformPeaks` which kicks off the fetch. */
export function getCachedPeaks(sampleId: string): Float32Array | null {
  return cache.get(sampleId) ?? null
}

/**
 * Return peaks for a sample, fetching + decoding on first request.
 * Subsequent calls return the cached array synchronously (the
 * promise resolves immediately when the id is cached).
 *
 * Resolves to `null` on fetch failure or decode failure — callers
 * should fall back to rendering a flat line. Transient errors are
 * NOT cached, so a later call will retry.
 */
export async function getWaveformPeaks(
  sample: Pick<Sample, 'id' | 'playUrl'>,
  buckets: number = DEFAULT_PEAK_BUCKETS,
): Promise<Float32Array | null> {
  const cached = cache.get(sample.id)
  if (cached) return cached
  const inFlight = pending.get(sample.id)
  if (inFlight) return inFlight
  const promise = (async () => {
    try {
      const res = await fetch(sample.playUrl)
      if (!res.ok) return null
      const bytes = await res.arrayBuffer()
      // Decode via OfflineAudioContext so we don't need a live
      // AudioContext (avoids a user-gesture dependency for callers
      // that render thumbnails before the engine has resumed).
      // Safari exposes the prefixed global; sniff both.
      const OfflineCtor =
        typeof OfflineAudioContext !== 'undefined'
          ? OfflineAudioContext
          : ((
              globalThis as unknown as {
                webkitOfflineAudioContext?: typeof OfflineAudioContext
              }
            ).webkitOfflineAudioContext ?? null)
      if (!OfflineCtor) return null
      // 1-frame, 1-channel, 44.1 kHz context is enough for decode —
      // the decoded buffer carries its own sampleRate + channel
      // layout regardless of the ctor args.
      const ctx = new OfflineCtor(1, 1, 44100)
      const buffer = await ctx.decodeAudioData(bytes)
      const peaks = downsampleToPeaks(buffer, buckets)
      cache.set(sample.id, peaks)
      return peaks
    } catch {
      return null
    } finally {
      pending.delete(sample.id)
    }
  })()
  pending.set(sample.id, promise)
  return promise
}

/** Mix the decoded buffer's channels into mono peaks bucketed into
 *  `bucketCount` min/max pairs. Multi-channel samples average
 *  their channels before peak detection — close enough for a
 *  thumbnail silhouette. */
function downsampleToPeaks(
  buffer: AudioBuffer,
  bucketCount: number,
): Float32Array {
  const channelCount = buffer.numberOfChannels
  const frames = buffer.length
  const channels: Float32Array[] = []
  for (let c = 0; c < channelCount; c++) channels.push(buffer.getChannelData(c))
  const samplesPerBucket = Math.max(1, Math.floor(frames / bucketCount))
  const peaks = new Float32Array(bucketCount * 2)
  for (let b = 0; b < bucketCount; b++) {
    const start = b * samplesPerBucket
    const end = Math.min(start + samplesPerBucket, frames)
    let min = 1
    let max = -1
    for (let i = start; i < end; i++) {
      let v = 0
      for (let c = 0; c < channelCount; c++) v += channels[c]![i] ?? 0
      v /= channelCount || 1
      if (v < min) min = v
      if (v > max) max = v
    }
    // Degenerate buckets (empty range) render as a flat line at 0.
    if (start >= end) {
      min = 0
      max = 0
    }
    peaks[b * 2] = min
    peaks[b * 2 + 1] = max
  }
  return peaks
}

/** Drop a sample's peaks from the cache — useful when the user
 *  deletes a local sample and we want a fresh decode if a new one
 *  happens to take the same id later. Silent no-op on unknown ids. */
export function evictPeaks(sampleId: string): void {
  cache.delete(sampleId)
}
